/**
 * Dashboard card for international (non-US) breeders: one-click
 * "tell me when payouts reach my country" capture. Feeds the
 * seller_payout_waitlist collection the admin uses to prioritize
 * cross-border Stripe Connect expansion.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GlobeHemisphereWestIcon as GlobeHemisphereWest, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

export default function SellerPayoutWaitlist() {
  const { user } = useAuth() || {};
  const [country, setCountry] = useState(null);
  const [joined, setJoined] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/me").then((r) => {
      const c = (r.data?.country || "US").toUpperCase();
      setCountry(c);
      if (c !== "US") {
        api.get("/me/seller-payout-waitlist")
          .then((r2) => setJoined(!!r2.data?.joined))
          .catch(() => setJoined(false));
      }
    }).catch(() => {});
  }, [user]);

  if (!user || !country || country === "US" || joined === null) return null;

  const join = async () => {
    setBusy(true);
    try {
      await api.post("/me/seller-payout-waitlist");
      setJoined(true);
      toast.success("Noted — we'll email you when payouts reach your country.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join the waitlist");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-8 rounded-2xl border border-[#EAEAEA] bg-white p-5 flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row"
      data-testid="seller-payout-waitlist-card"
    >
      <div className="flex items-start gap-3">
        <div className="bg-gold/10 border border-gold/30 rounded-lg p-2 flex-shrink-0">
          <GlobeHemisphereWest size={18} weight="duotone" className="text-[#8A6A00]" />
        </div>
        <div className="text-sm leading-relaxed">
          <p className="font-semibold text-ink">Want buyers to pay you through Armstrong?</p>
          <p className="text-[#6B6B6B] mt-0.5">
            Seller payouts aren&rsquo;t available in your country yet ({country}).
            Join the waitlist — countries with the most demand get enabled first.
          </p>
        </div>
      </div>
      {joined ? (
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 flex-shrink-0" data-testid="seller-payout-joined">
          <CheckCircle size={16} weight="fill" /> You&rsquo;re on the list
        </span>
      ) : (
        <Button
          onClick={join}
          disabled={busy}
          className="rounded-full bg-ink hover:bg-crimson text-white flex-shrink-0"
          data-testid="seller-payout-join-btn"
        >
          {busy ? "Joining…" : "Join the payout waitlist"}
        </Button>
      )}
    </div>
  );
}
